// ═══════════════════════════════════════════════════════════════════════════
// generatePDF — extraído VERBATIM de index.html v0.9.16.
//
// Único cambio respecto del original: la instanciación del documento pasa a
// inyectarse (crearDoc), para poder grabar los comandos de dibujo en pruebas.
// Ni una coordenada, ni una fuente, ni un color fueron tocados.
// ═══════════════════════════════════════════════════════════════════════════

import { LOGO_BASE64 } from "./logo.js";
import { etiquetaFolio } from "../datos/remisiones-pg.js";
import { guardarPDF } from "./guardar.js";
import { REMITENTE, fmt } from "./comunes.js";
import { SAT_FORMA_PAGO, SAT_METODO_PAGO, SAT_USO_CFDI } from "../fiscal/catalogos-sat.js";

function crearDocPredeterminado() {
  const { jsPDF } = window.jspdf;
  return new jsPDF({ unit: "mm", format: "letter" });
}

export function generatePDF(invoiceData, crearDoc = crearDocPredeterminado) {
  const doc = crearDoc();
  const pw = doc.internal.pageSize.getWidth();
  const ph = doc.internal.pageSize.getHeight();
  const ml = 18; // margin left
  const mr = pw - 18; // margin right
  let y = 18;

  const gold = [180, 150, 80];
  const dark = [40, 40, 40];
  const gray = [120, 120, 120];
  const lightGray = [200, 200, 200];

  // Helper: draw a line
  const hline = (yy, color = lightGray) => { doc.setDrawColor(...color); doc.setLineWidth(0.3); doc.line(ml, yy, mr, yy); };

  // ── HEADER WITH LOGO ──
  const logoW = 22;
  const logoH = 22.5;
  try { doc.addImage(LOGO_BASE64, "JPEG", ml, y - 4, logoW, logoH); } catch(e) { console.warn("Logo error:", e); }
  
  const textStartX = ml + logoW + 6;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.setTextColor(...dark);
  doc.text("ORDEN DE REMISIÓN", textStartX, y);
  
  doc.setFontSize(10);
  doc.setTextColor(...gray);
  const dateStr = invoiceData.date 
    ? new Date(invoiceData.date).toLocaleDateString("es-MX", { year: "numeric", month: "long", day: "numeric" })
    : new Date().toLocaleDateString("es-MX", { year: "numeric", month: "long", day: "numeric" });
  doc.text(dateStr, textStartX, y + 6);

  doc.setFontSize(9);
  doc.setTextColor(...gray);
  doc.text("FOLIO:", mr, y - 4, { align: "right" });
  doc.setFontSize(22);
  doc.setTextColor(...gold);
  doc.setFont("helvetica", "bold");
  doc.text(etiquetaFolio(invoiceData), mr, y + 4, { align: "right" });

  y += Math.max(16, logoH - 2);
  hline(y);
  y += 8;

  // ── ADDRESSES ──
  const midX = pw / 2;
  doc.setFontSize(7);
  doc.setTextColor(...gold);
  doc.setFont("helvetica", "bold");
  doc.text("REMITENTE", ml, y);
  doc.text("DESTINATARIO", midX + 4, y);
  y += 5;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...dark);
  const colW = midX - ml - 6; // available width per column
  doc.text(REMITENTE.name, ml, y);
  // Wrap client name
  const clientNameLines = doc.splitTextToSize(invoiceData.client.name, colW);
  doc.text(clientNameLines, midX + 4, y);
  y += Math.max(1, clientNameLines.length) * 4;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...gray);

  const remLines = [
    "RFC: " + REMITENTE.rfc,
    REMITENTE.street,
    REMITENTE.colonia + ", CP " + REMITENTE.cp,
    REMITENTE.city + ", " + REMITENTE.state,
  ];
  
  const cl = invoiceData.client;
  const destLines = [];
  if (cl.rfc) destLines.push("RFC: " + cl.rfc);
  if (cl.street) destLines.push(cl.street);
  if (cl.colonia || cl.cp) destLines.push((cl.colonia ? cl.colonia + ", " : "") + (cl.cp ? "CP " + cl.cp : ""));
  destLines.push((cl.city || "") + (cl.state ? ", " + cl.state : ""));
  if (cl.phone) destLines.push("Tel: " + cl.phone);
  if (cl.att) destLines.push("Att: " + cl.att);

  // Wrap each line to fit column width
  const wrapLines = (lines) => {
    const wrapped = [];
    lines.forEach(l => { if (l) doc.splitTextToSize(l, colW).forEach(wl => wrapped.push(wl)); });
    return wrapped;
  };
  const remWrapped = wrapLines(remLines);
  const destWrapped = wrapLines(destLines);

  const maxLines = Math.max(remWrapped.length, destWrapped.length);
  for (let i = 0; i < maxLines; i++) {
    if (remWrapped[i]) doc.text(remWrapped[i], ml, y);
    if (destWrapped[i]) doc.text(destWrapped[i], midX + 4, y);
    y += 4;
  }

  y += 2;
  hline(y);
  y += 6;

  // ── CFDI INFO ──
  doc.setFontSize(7.5);
  doc.setTextColor(...gray);
  const invUsoCfdi = invoiceData.usoCfdi || cl.cfdiUse || "G01";
  const invFormaPago = invoiceData.formaPago || cl.paymentMethod || "03";
  const invMetodoPago = invoiceData.metodoPago || cl.paymentTiming || "PUE";
  const usoCfdiLabel = SAT_USO_CFDI.find(u => u.code === invUsoCfdi)?.label || invUsoCfdi;
  const formaPagoLabel = SAT_FORMA_PAGO.find(f => f.code === invFormaPago)?.label || invFormaPago;
  const metodoPagoLabel = SAT_METODO_PAGO.find(m => m.code === invMetodoPago)?.label || invMetodoPago;
  const fullW = mr - ml;
  const cfdiLine1 = doc.splitTextToSize("USO DE CFDI: " + invUsoCfdi + " – " + usoCfdiLabel, fullW);
  doc.text(cfdiLine1, ml, y);
  y += cfdiLine1.length * 3.5;
  const cfdiLine2 = doc.splitTextToSize("FORMA DE PAGO: " + invFormaPago + " – " + formaPagoLabel + "    Método: " + invMetodoPago + " – " + metodoPagoLabel, fullW);
  doc.text(cfdiLine2, ml, y);
  y += cfdiLine2.length * 3.5 + 4;

  // ── TABLE HEADER ──
  const colX = { qty: ml, unit: ml + 14, desc: ml + 34, price: mr - 40, amount: mr };
  
  doc.setFillColor(245, 243, 238);
  doc.rect(ml - 2, y - 3.5, mr - ml + 4, 6, "F");
  
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7);
  doc.setTextColor(...gray);
  doc.text("CANT.", colX.qty, y);
  doc.text("UNIDAD", colX.unit, y);
  doc.text("DESCRIPCIÓN DE LA MERCANCIA", colX.desc, y);
  doc.text("P. BRUTO", colX.price, y, { align: "right" });
  doc.text("IMPORTE", colX.amount, y, { align: "right" });

  y += 5;
  hline(y, [220, 220, 220]);
  y += 5;

  // ── LINE ITEMS ──
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(...dark);

  const lines = invoiceData.lines || [];
  lines.forEach(li => {
    const name = li.productName || "";
    const price = li.unitPrice || 0;
    // Botellas del renglón que van sin cobrar (migración 0060). Las
    // remisiones anteriores no traen el campo, así que vale 0 y todo lo de
    // abajo se comporta EXACTAMENTE como antes — que es lo que comprueba el
    // golden master contra el monolito.
    const cortesia = li.cortesia || 0;
    const cobrables = li.qty - cortesia;
    const amount = li.amount ?? (cobrables * price);
    const isCortesia = price === 0 || cobrables <= 0;

    doc.text(String(li.qty), colX.qty, y);
    doc.text("botella", colX.unit, y);
    // El nombre lleva pegado cuántas van de regalo. Va aquí y no en una
    // columna nueva porque la remisión impresa la lee el cliente y el
    // transportista: lo que necesitan saber es que de esas 12 botellas, 2 no
    // se cobran — no una tabla más.
    doc.text((cortesia > 0 ? `${name} (${cortesia} de cortesía)` : name).substring(0, 50),
             colX.desc, y);
    if (isCortesia) {
      doc.setTextColor(100, 160, 160);
      doc.text("cortesía", colX.amount, y, { align: "right" });
      doc.setTextColor(...dark);
    } else {
      doc.text(fmt(price), colX.price, y, { align: "right" });
      doc.text(fmt(amount), colX.amount, y, { align: "right" });
    }
    y += 5;

    if (y > ph - 60) { doc.addPage(); y = 20; }
  });

  // ── TOTALS ROW ──
  y += 1;
  hline(y, dark);
  y += 5;
  doc.setFont("helvetica", "bold");
  doc.text(String(invoiceData.totalBottles), colX.qty, y);
  doc.text("BOTELLAS", colX.unit, y);
  doc.text(fmt(invoiceData.subtotalBeforeDiscount), colX.amount, y, { align: "right" });

  y += 10;

  // ── TOTALS SECTION ──
  const totX = mr - 50;
  
  if (invoiceData.discount > 0) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(180, 80, 80);
    doc.text("Descuento (" + invoiceData.discount + "%)", totX, y);
    doc.text("-" + fmt(invoiceData.discountAmount), mr, y, { align: "right" });
    y += 5;
  }

  doc.setTextColor(...gray);
  doc.setFont("helvetica", "normal");
  doc.text("Base gravable", totX, y);
  doc.setTextColor(...dark);
  doc.text(fmt(invoiceData.subtotal), mr, y, { align: "right" });
  y += 5;

  doc.setTextColor(...gray);
  // IEPS breakdown by rate
  const pdfIepsBreakdown = invoiceData.iepsBreakdown || [{ rate: invoiceData.iepsRate || 26.5, ieps: invoiceData.iepsAmount }];
  pdfIepsBreakdown.forEach(b => {
    doc.setTextColor(...gray);
    doc.text("IEPS (" + b.rate + "%)", totX, y);
    doc.setTextColor(...dark);
    doc.text(fmt(b.ieps), mr, y, { align: "right" });
    y += 5;
  });

  doc.setTextColor(...gray);
  doc.text("IVA (" + invoiceData.ivaRate + "%)", totX, y);
  doc.setTextColor(...dark);
  doc.text(fmt(invoiceData.ivaAmount), mr, y, { align: "right" });
  y += 3;

  hline(y, dark);
  y += 6;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(...dark);
  doc.text("TOTAL NETO", totX, y);
  doc.setTextColor(...gold);
  doc.text(fmt(invoiceData.total), mr, y, { align: "right" });

  y += 7;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...gray);
  doc.text("Total botellas: " + invoiceData.totalBottles, totX, y);

  y += 12;

  // ── BANK DETAILS ──
  if (y > ph - 45) { doc.addPage(); y = 20; }

  doc.setFillColor(248, 246, 242);
  doc.roundedRect(ml - 2, y - 4, mr - ml + 4, 24, 2, 2, "F");
  
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8.5);
  doc.setTextColor(...dark);
  doc.text("Datos bancarios", ml, y);
  y += 5;
  
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...gray);
  doc.text(REMITENTE.name, ml, y); y += 4;
  doc.text(REMITENTE.bank, ml, y); y += 4;
  doc.text("CLABE: " + REMITENTE.clabe, ml, y); y += 4;
  doc.text("Comprobante de pago: Whatsapp " + REMITENTE.whatsapp + " · " + REMITENTE.email, ml, y);
  y += 10;

  // ── NOTES ──
  if (invoiceData.notes) {
    if (y > ph - 25) { doc.addPage(); y = 20; }
    doc.setFillColor(255, 249, 230);
    doc.roundedRect(ml - 2, y - 4, mr - ml + 4, 12, 2, 2, "F");
    doc.setDrawColor(240, 224, 160);
    doc.roundedRect(ml - 2, y - 4, mr - ml + 4, 12, 2, 2, "S");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...dark);
    doc.text("Notas: ", ml, y);
    doc.setFont("helvetica", "normal");
    const noteLines = doc.splitTextToSize(invoiceData.notes, mr - ml - 14);
    doc.text(noteLines.slice(0, 3), ml + 12, y);
  }

  // ── FOOTER ──
  doc.setFontSize(7);
  doc.setTextColor(180, 180, 180);
  doc.text("Vinícola Total · Remisión #" + etiquetaFolio(invoiceData), pw / 2, ph - 8, { align: "center" });

  // Save
  const fileName = "Remision_" + etiquetaFolio(invoiceData) + "_" + (cl.name || "").replace(/[^a-zA-Z0-9áéíóúñÁÉÍÓÚÑ ]/g, "").replace(/ +/g, "_").substring(0, 30) + ".pdf";
  guardarPDF(doc, fileName);
}
